/**
 * LE TEMPS FORT : LE LANCEUR SE RAPPELLE A L'ATTENTION, PUIS SE TAIT.
 *
 * POURQUOI IL EXISTE. Un lanceur immobile dans un coin devient un meuble en trois secondes. Un
 * mouvement bref, de temps en temps, rattrape un regard sans rien exiger de personne.
 *
 * IL SE TAIT APRES LA PREMIERE OUVERTURE, ET C'EST LA REGLE QUI COMPTE. Quelqu'un qui a deja
 * ouvert la conversation sait que le lanceur est la ; continuer a l'agiter n'informe plus, ca
 * harcele. Il ne revient qu'a une seule condition : une reponse arrive pendant que le panneau est
 * ferme. C'est le meme signal que le point rouge, `handleNotificationDot`.
 *
 * JAMAIS PENDANT QUE LE PANNEAU EST OUVERT. Le lanceur y montre sa croix ; l'agiter derriere un
 * panneau ouvert ne veut rien dire, et la collision « le visiteur ouvre pendant le temps fort »
 * se regle du cote de la bascule, qui efface `attention` avant de poser `ouvert`.
 *
 * IL NE SE DECLENCHE PAS DU TOUT SI LE VISITEUR A DEMANDE MOINS DE MOUVEMENT. La feuille
 * neutraliserait deja les animations ; programmer une cadence qui ne produit rien serait du bruit
 * sur la page du client.
 *
 * IL NE MEMORISE RIEN CHEZ LE VISITEUR, comme l'accroche : la portee est la page, pas la visite.
 * Sur un site ou l'on navigue, la cadence repart a chaque page. C'est dit, pas cache.
 */
const PREMIER_DELAI = 2000;
const CADENCE = 10000;
const DUREE = 2400;

let cible = null;
let premier = null;
let cadence = null;
let retour = null;

const sansMouvement = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const declencher = () => {
  if (!cible) return;
  const { conteneur, gestes } = cible;
  if (conteneur.dataset.etat === 'ouvert') return;

  // REPOS PUIS UNE MESURE RELUE, AVANT `attention`. Les keyframes ponctuelles (le frisson d'Orbe,
  // l'eclat de Phare) ne rejouent que si le navigateur a vu l'element quitter l'etat entre-temps ;
  // lire une dimension le force a recalculer tout de suite, sans attendre le cadre suivant.
  conteneur.dataset.etat = 'repos';
  conteneur.getBoundingClientRect();
  conteneur.dataset.etat = 'attention';
  if (gestes && typeof gestes.attention === 'function')
    gestes.attention(conteneur);

  if (retour) window.clearTimeout(retour);
  retour = window.setTimeout(() => {
    retour = null;
    if (conteneur.dataset.etat === 'attention')
      conteneur.dataset.etat = 'repos';
  }, DUREE);
};

export const demarrerLeTempsFort = ({ conteneur, gestes }) => {
  if (!conteneur || sansMouvement()) return;
  cible = { conteneur, gestes };
  premier = window.setTimeout(() => {
    premier = null;
    declencher();
    cadence = window.setInterval(declencher, CADENCE);
  }, PREMIER_DELAI);
};

/**
 * Appele a la premiere ouverture. La cible reste connue : c'est elle que le reveil ranime quand
 * une reponse arrive plus tard.
 */
export const arreterLeTempsFort = () => {
  if (premier) window.clearTimeout(premier);
  if (cadence) window.clearInterval(cadence);
  premier = null;
  cadence = null;
};

/**
 * UN SEUL BATTEMENT, PAS UNE CADENCE QUI REPART. Une reponse est arrivee : le lanceur le signale
 * une fois. Relancer les dix secondes ferait clignoter le coin de l'ecran de quelqu'un qui a
 * choisi de ne pas rouvrir.
 */
export const reveillerLeTempsFort = () => {
  if (!cible || sansMouvement()) return;
  if (window.$chatwoot && window.$chatwoot.isOpen) return;
  declencher();
};

export const oublierLeTempsFort = () => {
  arreterLeTempsFort();
  if (retour) window.clearTimeout(retour);
  retour = null;
  cible = null;
};
